import { useEffect, useRef, useState } from 'react';
import type { DayView } from '../lib/day';
import { dayApi, sectionEditable } from '../lib/day';
import { formatDate, formatLitres, formatRs } from '../lib/money';
import type { CostCode } from '../lib/costcodes';
import CostCodeSelect from '../components/CostCodeSelect';

/**
 * Diesel: the rate for the day, litres issued per plant with the cash value
 * calculated, and an Account button per plant showing its 7-day and 30-day cost
 * day by day.
 *
 * The rate is permission-gated (`rate`). It is the day's one price: changing it
 * re-values every litre on the day, on the Worker, while Diesel is editable.
 */

interface Plant {
  id: string;
  code: string;
  name: string;
  /** Its usual cost code: where its fuel starts each day. */
  cost_code_id?: string | null;
}

interface Props {
  view: DayView;
  plants: Plant[];
  costCodes: CostCode[];
  isOwner: boolean;
  canSetRate: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
}

export default function Diesel({
  view, plants, costCodes, isOwner, canSetRate, onChanged, onError,
}: Props) {
  const editable = sectionEditable(view, 'diesel', isOwner);
  const date = view.day.workDate;

  const [rate, setRate] = useState(String(view.day.dieselRateRs));
  const [openPlant, setOpenPlant] = useState<string | null>(null);

  useEffect(() => setRate(String(view.day.dieselRateRs)), [view.day.dieselRateRs]);

  const litresFor = (plantId: string) =>
    view.fuel.find((row) => row.plant_id === plantId)?.litres_x100 ?? 0;
  const valueFor = (plantId: string) =>
    view.fuel.find((row) => row.plant_id === plantId)?.value_rs ?? 0;
  const rateFor = (plantId: string) =>
    view.fuel.find((row) => row.plant_id === plantId)?.rate_rs ?? view.day.dieselRateRs;

  // Saved ~800ms after the last change as well as on blur, like the litres:
  // the arrows on a laptop never blur the field.
  const rateTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(rateTimer.current), []);

  async function commitRate(typed = rate) {
    window.clearTimeout(rateTimer.current);
    const value = Math.trunc(Number(typed) || 0);
    // The same price again still goes through if a row sits at another one,
    // so a day entered before rows followed the price can be put right.
    if (
      value === view.day.dieselRateRs &&
      view.fuel.every((row) => row.rate_rs === value)
    ) return;
    try {
      await dayApi.setRate(date, value);
      onChanged();
    } catch (err) {
      setRate(String(view.day.dieselRateRs));
      onError(err instanceof Error ? err.message : 'Could not change the rate.');
    }
  }

  /** The day's code for this plant's fuel: the entry's once there is one, else the plant's own. */
  const codeFor = (plant: Plant): string | null => {
    const row = view.fuel.find((f) => f.plant_id === plant.id);
    return row ? row.cost_code_id ?? null : plant.cost_code_id ?? null;
  };

  async function commitCode(plantId: string, costCodeId: string | null) {
    try {
      await dayApi.setFuelCode(date, plantId, costCodeId);
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not change the cost code.');
    }
  }

  async function commitLitres(plantId: string, litres: string) {
    const litresX100 = Math.max(0, Math.round((Number(litres) || 0) * 100));
    if (litresX100 === litresFor(plantId)) return;
    try {
      await dayApi.setFuel(date, plantId, litresX100);
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save those litres.');
    }
  }

  return (
    <>
      <div className="card">
        <div className="row" style={{ paddingTop: 0 }}>
          <div>
            <h3>Diesel rate today</h3>
            <div className="sub">
              {canSetRate ? 'Update this when the price changes' : 'Owner sets this rate'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="number"
              min="0"
              value={rate}
              disabled={!editable || !canSetRate}
              onChange={(e) => {
                const typed = e.target.value;
                setRate(typed);
                window.clearTimeout(rateTimer.current);
                rateTimer.current = window.setTimeout(() => void commitRate(typed), 800);
              }}
              onBlur={() => void commitRate()}
              style={{ width: 110, textAlign: 'right' }}
            />
            <span className="sub">/ litre</span>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Fuel issued per plant</h3>
        <div className="sub">
          Cash given to the operator to fill up. Tap Account to see what each plant is
          costing.{costCodes.length > 0 && ' Cost code starts as the plant’s usual code.'}
        </div>

        {plants.length === 0 && (
          <div className="empty">No plants yet. They are added on the Permits tab.</div>
        )}

        {plants.map((plant) => (
          <div key={plant.id}>
            <div className="row">
              <div>
                <div className="name">{plant.name}</div>
                <div className="sub">
                  {plant.code}
                  {litresFor(plant.id) > 0 && rateFor(plant.id) !== view.day.dieselRateRs && (
                    <> · issued at {formatRs(rateFor(plant.id))}/L</>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  flexWrap: 'wrap', justifyContent: 'flex-end',
                }}
              >
                {costCodes.length > 0 && (
                  <div className="cc-pick">
                    <CostCodeSelect
                      codes={costCodes}
                      value={codeFor(plant)}
                      usual={plant.cost_code_id ?? null}
                      disabled={!editable}
                      onChange={(costCodeId) => void commitCode(plant.id, costCodeId)}
                    />
                  </div>
                )}
                <LitresInput
                  litresX100={litresFor(plant.id)}
                  disabled={!editable}
                  onCommit={(value) => commitLitres(plant.id, value)}
                />
                <span className="sub">L</span>
                <span style={{ minWidth: 88, textAlign: 'right' }}>
                  {formatRs(valueFor(plant.id))}
                </span>
                <button
                  type="button"
                  className="btn sm"
                  style={{ minWidth: 0 }}
                  onClick={() => setOpenPlant(openPlant === plant.id ? null : plant.id)}
                >
                  {openPlant === plant.id ? 'Hide' : 'Account'}
                </button>
              </div>
            </div>
            {openPlant === plant.id && (
              <PlantAccount plant={plant} on={date} onError={onError} />
            )}
          </div>
        ))}

        {/* Fuel on a plant removed since it was issued. It still counts in the
            day (a removed plant is deactivated, never deleted) and stayed
            invisible until 23 September 2026 — "50 L when nothing is entered".
            Shown so it can be seen, and zeroed if it was a mistake. */}
        {view.fuel
          .filter((row) => row.litres_x100 > 0 && !plants.some((plant) => plant.id === row.plant_id))
          .map((row) => (
            <div className="row" key={row.plant_id}>
              <div>
                <div className="name">{row.plant_name ?? 'Removed plant'}</div>
                <div className="sub">
                  {row.plant_code ? `${row.plant_code} · ` : ''}
                  removed plant — still counts today. Set it to 0 if it was a mistake.
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <LitresInput
                  litresX100={row.litres_x100}
                  disabled={!editable}
                  onCommit={(value) => commitLitres(row.plant_id, value)}
                />
                <span className="sub">L</span>
                <span style={{ minWidth: 88, textAlign: 'right' }}>{formatRs(row.value_rs)}</span>
              </div>
            </div>
          ))}
      </div>

      <div className="grid2">
        <div className="metric">
          <div className="k">Litres today</div>
          <div className="v">{formatLitres(view.totals.litresX100)} L</div>
        </div>
        <div className="metric">
          <div className="k">Cash issued</div>
          <div className="v">{formatRs(view.totals.dieselRs)}</div>
        </div>
      </div>
    </>
  );
}

/**
 * The litres box. Saves ~800ms after the last change as well as on blur —
 * the arrows on a laptop never blur the field, so on blur alone the totals
 * under the table stood still until the clerk clicked somewhere else (the
 * owner, 23 September 2026). Whole litres on the arrows; a typed fraction
 * still goes through as hundredths.
 */
function LitresInput({
  litresX100, disabled, onCommit,
}: {
  litresX100: number;
  disabled: boolean;
  onCommit: (value: string) => Promise<void>;
}) {
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef<string | null>(null);
  const flush = () => {
    window.clearTimeout(timer.current);
    if (pending.current === null) return;
    const value = pending.current;
    pending.current = null;
    void onCommit(value);
  };
  useEffect(() => flush, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <input
      type="number"
      min="0"
      step="1"
      defaultValue={litresX100 ? formatLitres(litresX100) : ''}
      disabled={disabled}
      onChange={(e) => {
        pending.current = e.target.value;
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(flush, 800);
      }}
      onBlur={flush}
      style={{ width: 84, textAlign: 'right' }}
    />
  );
}

// ------------------------------------------------------------- the account

function PlantAccount({
  plant, on, onError,
}: {
  plant: Plant;
  on: string;
  onError: (message: string) => void;
}) {
  const [data, setData] = useState<Awaited<ReturnType<typeof dayApi.plantAccount>> | null>(null);

  useEffect(() => {
    let live = true;
    dayApi
      .plantAccount(plant.id, on)
      .then((result) => { if (live) setData(result); })
      .catch((err: unknown) =>
        onError(err instanceof Error ? err.message : 'Could not load that account.'),
      );
    return () => { live = false; };
  }, [plant.id, on, onError]);

  if (!data) return <div className="empty">Loading…</div>;

  return (
    <div
      style={{
        background: 'var(--surface-2)', borderRadius: 12,
        padding: '12px 14px', margin: '0 -4px 8px',
      }}
    >
      <div style={{ fontWeight: 500, marginBottom: 8 }}>{plant.name} — account</div>

      <div className="grid2" style={{ marginBottom: 10 }}>
        <div className="metric" style={{ background: 'var(--surface)' }}>
          <div className="k">Last 7 days</div>
          <div className="v">{formatRs(data.last7.totalRs)}</div>
          <div className="k">
            {formatLitres(data.last7.litresX100)} L diesel · {formatRs(data.last7.miscRs)} other
          </div>
        </div>
        <div className="metric" style={{ background: 'var(--surface)' }}>
          <div className="k">Last 30 days</div>
          <div className="v">{formatRs(data.last30.totalRs)}</div>
          <div className="k">
            {formatLitres(data.last30.litresX100)} L diesel · {formatRs(data.last30.miscRs)} other
          </div>
        </div>
      </div>

      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th className="num">Litres</th>
              <th className="num">Diesel</th>
              <th className="num">Other</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.days.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">Nothing in this period.</td>
              </tr>
            )}
            {data.days.map((row) => (
              <tr key={row.work_date}>
                <td>{formatDate(row.work_date)}</td>
                <td className="num">{formatLitres(row.litres_x100)}</td>
                <td className="num">{formatRs(row.diesel_rs)}</td>
                <td className="num">{row.misc_rs ? formatRs(row.misc_rs) : '—'}</td>
                <td className="num">{formatRs(row.total_rs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.miscItems.length > 0 && (
        <>
          <div className="sub" style={{ marginTop: 10 }}>Other costs booked to this plant</div>
          {data.miscItems.map((item, index) => (
            <div className="ln" key={`${item.work_date}-${index}`}>
              <span className="k">
                {formatDate(item.work_date)} · {item.description}
              </span>
              <span>{formatRs(item.amount_rs)}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
